<script lang="ts">
  import { onMount } from 'svelte';
  import { writable, type Writable } from 'svelte/store';
  import SvgMapViewer from './SvgMapViewer.svelte';
  import { preprocessSvg, type Floor, type FloorMap } from './mapUtils';
  import { getStoresContext } from '~/stores';
  import { DEBUG_CONFIG } from './mapConstants';
  
  // Define a type for the viewport stats
  interface ViewportStats {
    x: number;
    y: number;
    width: number;
    height: number;
    zoomLevel: number;
    atMinZoom: boolean;
    atMaxZoom: boolean;
    atPanBoundary: boolean;
  }
  
  // Props
  export let request_store_key;
  export let initialStoreId: string | null = null;
  export let autoZoomToStore: boolean = false;
  export let mapConfig: any = {};
  
  // Set debug panel state from config
  if (mapConfig.showDebugPanels !== undefined) {
    DEBUG_CONFIG.update(config => ({ ...config, showDebugPanels: mapConfig.showDebugPanels }));
  }

  // Get stores from context
  const { floors: floorsStore } = getStoresContext(request_store_key);
  
  // State
  let processedFloors: Floor[] = [];
  let currentFloorId: number | null = null;
  let highlightedStoreId: Writable<string | null> = writable(initialStoreId);
  let svgContent: string = '';
  let isLoading: boolean = true;
  let error: string | null = null;
  
  // Process floors when they change
  $: if ($floorsStore) {
    processFloors([...$floorsStore]);
  }
  
  // When current floor changes, load the appropriate SVG
  $: if (currentFloorId !== null && processedFloors.length > 0) {
    loadFloorSvg(currentFloorId);
  }
  
  // Process floor data
  function processFloors(floorsData: any[]) {
    try {
      if (!floorsData || floorsData.length === 0) {
        throw new Error('No floor data available');
      }
      
      processedFloors = floorsData
        .filter(floor => floor.floorMaps?.some((map: any) => map && map.type === 'SVG' && map.content))
        .sort((a, b) => a.order - b.order);
      
      if (processedFloors.length === 0) {
        throw new Error('No floors with SVG content available');
      }
      
      if (currentFloorId === null) {
        currentFloorId = processedFloors[0].id;
      }
    } catch (err) {
      error = err instanceof Error ? err.message : 'Error processing floor data';
      isLoading = false;
    }
  }
  
  // Load SVG for a specific floor
  async function loadFloorSvg(floorId: number) {
    isLoading = true;
    error = null;
    try {
      const floor = processedFloors.find(f => f.id === floorId);
      if (!floor) throw new Error(`Floor with ID ${floorId} not found`);
      const svgMap = floor.floorMaps?.find((map: FloorMap) => map?.type === 'SVG' && map.content);
      if (!svgMap?.content) throw new Error('No SVG content found for this floor');
      svgContent = preprocessSvg(svgMap.content);
    } catch (err) {
      error = err instanceof Error ? err.message : 'Error loading floor SVG';
    } finally {
      isLoading = false;
    }
  }
  
  // Handle store interactions
  function handleStoreClick(storeId: string) {
    highlightedStoreId.set(storeId);
  }
  
  function handleStoreHover(storeId: string | null) {
    // Future use for tooltips, etc.
  }
  
  onMount(() => {
    if (initialStoreId) {
      highlightedStoreId.set(initialStoreId);
    }
  });
</script>

<div class="svg-map-app">
  {#if isLoading}
    <div class="loading">Loading map data...</div>
  {:else if error}
    <div class="error">
      <p>Error: {error}</p>
    </div>
  {:else}
    <SvgMapViewer
      {svgContent}
      floors={processedFloors}
      bind:currentFloorId
      highlightedStoreId={$highlightedStoreId}
      {autoZoomToStore}
      {mapConfig}
      {request_store_key}
      on:storeselect={(e) => handleStoreClick(e.detail.storeId)}
      on:storeHover={(e) => handleStoreHover(e.detail.storeId)}
    />
  {/if}
</div>

<style>
  .svg-map-app {
    position: relative;
    width: 100%;
    height: 100%;
  }
  
  .loading, .error {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    text-align: center;
  }
  
  .error {
    color: #e53935;
  }
</style> 